import { Selection, NodeSelection } from 'prosemirror-state';
import { Slice } from 'prosemirror-model';
// ::- Gap cursor selections are represented using this class. Its
// `$anchor` and `$head` properties both point at the cursor position.
export class GapCursor extends Selection {
    // : (ResolvedPos)
    constructor($pos) {
        super($pos, $pos);
    }
    map(doc, mapping) {
        const $pos = doc.resolve(mapping.map(this.head));
        return GapCursor.valid($pos) ? new GapCursor($pos) : Selection.near($pos);
    }
    content() {
        return Slice.empty;
    }
    eq(other) {
        return other instanceof GapCursor && other.head === this.head;
    }
    toJSON() {
        return { type: 'ex-gapcursor', pos: this.head };
    }
    static fromJSON(doc, json) {
        if (typeof json.pos !== 'number')
            throw new RangeError('Invalid input for GapCursor.fromJSON');
        return new GapCursor(doc.resolve(json.pos));
    }
    getBookmark() {
        return new GapBookmark(this.anchor);
    }
    static valid($pos) {
        const { parent } = $pos;
        // ExEditor patch: 额外允许文档首部的一些块前放置 GapCursor
        if (parent === $pos.doc &&
            $pos.pos === 0 &&
            parent.firstChild &&
            (parent.firstChild.type.spec.code || !parent.firstChild.isTextblock)) {
            return true;
        }
        // ExEditor patch: 额外允许文档尾部的一些块后放置 GapCursor
        // 这里需要配套使用 tailGapcursorBackspace
        if (parent === $pos.doc &&
            $pos.pos === $pos.doc.nodeSize - 2 &&
            parent.lastChild &&
            (parent.lastChild.type.spec.code || !parent.lastChild.isTextblock)) {
            return true;
        }
        if (parent.isTextblock || !closedBefore($pos) || !closedAfter($pos))
            return false;
        const override = parent.type.spec.allowGapCursor;
        if (override != null)
            return override;
        const deflt = parent.contentMatchAt($pos.index()).defaultType;
        return deflt === null || deflt === void 0 ? void 0 : deflt.isTextblock;
    }
    static findFrom($pos, dir, mustMove) {
        // eslint-disable-next-line no-restricted-syntax
        search: for (;;) {
            if (!mustMove && GapCursor.valid($pos))
                return new GapCursor($pos);
            let { pos } = $pos;
            let next = null;
            // Scan up from this position
            for (let d = $pos.depth;; d--) {
                const parent = $pos.node(d);
                if (dir > 0 ? $pos.indexAfter(d) < parent.childCount : $pos.index(d) > 0) {
                    next = parent.child(dir > 0 ? $pos.indexAfter(d) : $pos.index(d) - 1);
                    break;
                }
                else if (d === 0) {
                    return null;
                }
                pos += dir;
                const $cur = $pos.doc.resolve(pos);
                if (GapCursor.valid($cur))
                    return new GapCursor($cur);
            }
            // And then down into the next node
            for (;;) {
                const inside = dir > 0 ? next.firstChild : next.lastChild;
                if (!inside) {
                    if (next.isAtom &&
                        !next.isText &&
                        !NodeSelection.isSelectable(next)) {
                        /* eslint-disable no-param-reassign */
                        $pos = $pos.doc.resolve(pos + next.nodeSize * dir);
                        mustMove = false;
                        continue search;
                    }
                    break;
                }
                next = inside;
                pos += dir;
                const $cur = $pos.doc.resolve(pos);
                if (GapCursor.valid($cur))
                    return new GapCursor($cur);
            }
            return null;
        }
    }
}
GapCursor.prototype.visible = false;
Selection.jsonID('ex-gapcursor', GapCursor);
class GapBookmark {
    constructor(pos) {
        this.pos = pos;
    }
    map(mapping) {
        return new GapBookmark(mapping.map(this.pos));
    }
    resolve(doc) {
        const $pos = doc.resolve(this.pos);
        return GapCursor.valid($pos) ? new GapCursor($pos) : Selection.near($pos);
    }
}
function closedBefore($pos) {
    for (let d = $pos.depth; d >= 0; d--) {
        const index = $pos.index(d);
        const parent = $pos.node(d);
        // At the start of this parent, look at next one
        if (index === 0) {
            if (parent.type.spec.isolating)
                return true;
            continue;
        }
        // See if the node before (or its first ancestor) is closed
        for (let before = $pos.node(d).child(index - 1);; before = before.lastChild) {
            if ((before.childCount === 0 && !before.inlineContent) ||
                before.isAtom ||
                before.type.spec.isolating)
                return true;
            if (before.inlineContent)
                return false;
        }
    }
    // Hit start of document
    return true;
}
function closedAfter($pos) {
    for (let d = $pos.depth; d >= 0; d--) {
        const index = $pos.indexAfter(d);
        const parent = $pos.node(d);
        if (index === parent.childCount) {
            if (parent.type.spec.isolating)
                return true;
            continue;
        }
        for (let after = parent.child(index);; after = after.firstChild) {
            if ((after.childCount === 0 && !after.inlineContent) ||
                after.isAtom ||
                after.type.spec.isolating)
                return true;
            if (after.inlineContent)
                return false;
        }
    }
    return true;
}
//# sourceMappingURL=gapcursor.js.map